<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div
        class="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[min(90vh,720px)] overflow-y-auto relative"
        @click.stop
      >
        <button
          type="button"
          class="absolute right-3 top-3 p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          aria-label="关闭"
          @click="close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>

        <div class="p-6 pt-10">
          <div class="flex mb-5 border-b border-gray-200" role="tablist">
            <button
              type="button"
              role="tab"
              :aria-selected="mainTab === 'login'"
              :class="[
                'flex-1 py-3 text-center text-sm font-semibold transition-all duration-200 border-b-2 -mb-px',
                mainTab === 'login'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              ]"
              @click="mainTab = 'login'"
            >
              登录
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="mainTab === 'register'"
              :class="[
                'flex-1 py-3 text-center text-sm font-semibold transition-all duration-200 border-b-2 -mb-px',
                mainTab === 'register'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              ]"
              @click="mainTab = 'register'"
            >
              注册
            </button>
          </div>

          <!-- 登录 -->
          <div v-show="mainTab === 'login'">
            <p class="text-sm text-gray-500 mb-4">已注册用户可使用验证码或密码登录；未注册请先切换到「注册」。</p>
            <div class="flex rounded-xl bg-gray-100 p-1 mb-4">
              <button
                type="button"
                :class="[
                  'flex-1 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors',
                  loginMode === 'otp' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                ]"
                @click="loginMode = 'otp'"
              >
                验证码
              </button>
              <button
                type="button"
                :class="[
                  'flex-1 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors',
                  loginMode === 'pwd' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                ]"
                @click="loginMode = 'pwd'"
              >
                密码
              </button>
            </div>
            <form @submit.prevent="handleLogin" class="space-y-3">
              <div>
                <label class="block text-sm text-gray-700 mb-1">手机号或邮箱</label>
                <input
                  v-model="account"
                  type="text"
                  autocomplete="username"
                  placeholder="11 位手机号或邮箱"
                  class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <template v-if="loginMode === 'otp'">
                <div class="flex gap-2">
                  <div class="flex-1">
                    <label class="block text-sm text-gray-700 mb-1">验证码</label>
                    <input
                      v-model="code"
                      type="text"
                      inputmode="numeric"
                      maxlength="6"
                      autocomplete="one-time-code"
                      placeholder="6 位数字"
                      class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div class="flex items-end">
                    <button
                      type="button"
                      :disabled="sendingCode || cooldown > 0"
                      class="whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                      @click="sendCode"
                    >
                      {{ cooldown > 0 ? `${cooldown}s` : '获取验证码' }}
                    </button>
                  </div>
                </div>
              </template>
              <div v-else>
                <label class="block text-sm text-gray-700 mb-1">密码</label>
                <input
                  v-model="password"
                  type="password"
                  autocomplete="current-password"
                  placeholder="注册时设置的密码"
                  class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p v-if="devCodeHint" class="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                开发环境验证码：<span class="font-mono font-semibold">{{ devCodeHint }}</span>
              </p>
              <p v-if="errorLogin" class="text-sm text-red-600">{{ errorLogin }}</p>
              <button
                type="submit"
                :disabled="loadingLogin"
                class="w-full bg-blue-600 text-white rounded-lg py-2.5 hover:bg-blue-700 disabled:opacity-60 font-medium"
              >
                {{ loadingLogin ? '登录中...' : '登录' }}
              </button>
            </form>
          </div>

          <!-- 注册 -->
          <div v-show="mainTab === 'register'">
            <p class="text-sm text-gray-500 mb-4">收取验证码并设置密码（至少 8 位）。</p>
            <form @submit.prevent="handleRegister" class="space-y-3">
              <div>
                <label class="block text-sm text-gray-700 mb-1">手机号或邮箱</label>
                <input
                  v-model="regAccount"
                  type="text"
                  autocomplete="username"
                  placeholder="11 位手机号或邮箱"
                  class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div class="flex gap-2">
                <div class="flex-1">
                  <label class="block text-sm text-gray-700 mb-1">验证码</label>
                  <input
                    v-model="regCode"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    autocomplete="one-time-code"
                    placeholder="6 位数字"
                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div class="flex items-end">
                  <button
                    type="button"
                    :disabled="sendingCodeReg || cooldownReg > 0"
                    class="whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    @click="sendCodeRegister"
                  >
                    {{ cooldownReg > 0 ? `${cooldownReg}s` : '获取验证码' }}
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm text-gray-700 mb-1">密码</label>
                <input
                  v-model="regPassword"
                  type="password"
                  autocomplete="new-password"
                  placeholder="至少 8 位"
                  class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label class="block text-sm text-gray-700 mb-1">确认密码</label>
                <input
                  v-model="regPasswordConfirm"
                  type="password"
                  autocomplete="new-password"
                  placeholder="再次输入密码"
                  class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p v-if="devCodeHintReg" class="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                开发环境验证码：<span class="font-mono font-semibold">{{ devCodeHintReg }}</span>
              </p>
              <p v-if="errorRegister" class="text-sm text-red-600">{{ errorRegister }}</p>
              <button
                type="submit"
                :disabled="loadingRegister"
                class="w-full bg-blue-600 text-white rounded-lg py-2.5 hover:bg-blue-700 disabled:opacity-60 font-medium"
              >
                {{ loadingRegister ? '注册中...' : '注册并登录' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { setAuth } from '../utils/auth';
import { parseLoginAccountInput } from '../utils/loginAccount';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** 打开时选中的主标签 */
  initialTab: { type: String, default: 'login' },
  /** 登录/注册成功后的跳转路径 */
  redirectAfterAuth: { type: String, default: '/single' },
});

const emit = defineEmits(['update:modelValue', 'success']);

const router = useRouter();

const mainTab = ref('login');
const loginMode = ref('otp');
const account = ref('');
const code = ref('');
const password = ref('');
const loadingLogin = ref(false);
const errorLogin = ref('');
const devCodeHint = ref('');
const sendingCode = ref(false);
const cooldown = ref(0);
let cooldownTimer = null;

const regAccount = ref('');
const regCode = ref('');
const regPassword = ref('');
const regPasswordConfirm = ref('');
const loadingRegister = ref(false);
const errorRegister = ref('');
const devCodeHintReg = ref('');
const sendingCodeReg = ref(false);
const cooldownReg = ref(0);
let cooldownTimerReg = null;

function startCooldown(sec, isReg) {
  if (isReg) {
    if (cooldownTimerReg) clearInterval(cooldownTimerReg);
    cooldownReg.value = sec;
    cooldownTimerReg = setInterval(() => {
      cooldownReg.value -= 1;
      if (cooldownReg.value <= 0) {
        clearInterval(cooldownTimerReg);
        cooldownTimerReg = null;
      }
    }, 1000);
    return;
  }
  if (cooldownTimer) clearInterval(cooldownTimer);
  cooldown.value = sec;
  cooldownTimer = setInterval(() => {
    cooldown.value -= 1;
    if (cooldown.value <= 0) {
      clearInterval(cooldownTimer);
      cooldownTimer = null;
    }
  }, 1000);
}

function resetAll() {
  loginMode.value = 'otp';
  account.value = '';
  code.value = '';
  password.value = '';
  errorLogin.value = '';
  devCodeHint.value = '';
  regAccount.value = '';
  regCode.value = '';
  regPassword.value = '';
  regPasswordConfirm.value = '';
  errorRegister.value = '';
  devCodeHintReg.value = '';
}

function close() {
  emit('update:modelValue', false);
}

function afterAuthSuccess(kind) {
  emit('success', { kind });
  emit('update:modelValue', false);
  const r = props.redirectAfterAuth || '/single';
  router.replace(typeof r === 'string' && r.startsWith('/') ? r : '/single');
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      mainTab.value = props.initialTab === 'register' ? 'register' : 'login';
      resetAll();
    }
  }
);

watch(
  () => props.initialTab,
  (t) => {
    if (props.modelValue) {
      mainTab.value = t === 'register' ? 'register' : 'login';
    }
  }
);

const sendCode = async () => {
  errorLogin.value = '';
  devCodeHint.value = '';
  const parsed = parseLoginAccountInput(account.value);
  if (!parsed.ok) {
    errorLogin.value = parsed.error;
    return;
  }
  sendingCode.value = true;
  try {
    const res = await axios.post('/api/send-login-code', { account: parsed.account });
    if (res?.data?.code !== 0) throw new Error(res?.data?.message || '发送失败');
    const data = res.data.data || {};
    if (data.dev_code) devCodeHint.value = data.dev_code;
    startCooldown(60, false);
  } catch (err) {
    errorLogin.value = err?.response?.data?.message || err.message || '发送验证码失败';
  } finally {
    sendingCode.value = false;
  }
};

const sendCodeRegister = async () => {
  errorRegister.value = '';
  devCodeHintReg.value = '';
  const parsed = parseLoginAccountInput(regAccount.value);
  if (!parsed.ok) {
    errorRegister.value = parsed.error;
    return;
  }
  sendingCodeReg.value = true;
  try {
    const res = await axios.post('/api/send-login-code', { account: parsed.account });
    if (res?.data?.code !== 0) throw new Error(res?.data?.message || '发送失败');
    const data = res.data.data || {};
    if (data.dev_code) devCodeHintReg.value = data.dev_code;
    startCooldown(60, true);
  } catch (err) {
    errorRegister.value = err?.response?.data?.message || err.message || '发送验证码失败';
  } finally {
    sendingCodeReg.value = false;
  }
};

const handleLogin = async () => {
  errorLogin.value = '';
  const parsed = parseLoginAccountInput(account.value);
  if (!parsed.ok) {
    errorLogin.value = parsed.error;
    return;
  }
  loadingLogin.value = true;
  try {
    let body;
    if (loginMode.value === 'pwd') {
      const p = password.value;
      if (!p) {
        errorLogin.value = '请输入密码';
        loadingLogin.value = false;
        return;
      }
      body = { account: parsed.account, password: p };
    } else {
      const c = code.value.trim();
      if (!c) {
        errorLogin.value = '请输入验证码';
        loadingLogin.value = false;
        return;
      }
      if (c.length !== 6) {
        errorLogin.value = '验证码为 6 位数字';
        loadingLogin.value = false;
        return;
      }
      body = { account: parsed.account, code: c };
    }
    const res = await axios.post('/api/login', body);
    if (res?.data?.code !== 0) throw new Error(res?.data?.message || '登录失败');
    const data = res.data.data;
    setAuth(data.token, data.user);
    afterAuthSuccess('login');
  } catch (err) {
    errorLogin.value = err?.response?.data?.message || err.message || '登录失败';
  } finally {
    loadingLogin.value = false;
  }
};

const MIN_PWD = 8;

const handleRegister = async () => {
  errorRegister.value = '';
  const parsed = parseLoginAccountInput(regAccount.value);
  if (!parsed.ok) {
    errorRegister.value = parsed.error;
    return;
  }
  const c = regCode.value.trim();
  if (!c) {
    errorRegister.value = '请输入验证码';
    return;
  }
  if (c.length !== 6) {
    errorRegister.value = '验证码为 6 位数字';
    return;
  }
  const p1 = regPassword.value;
  const p2 = regPasswordConfirm.value;
  if (!p1 || !p2) {
    errorRegister.value = '请输入密码并再次确认';
    return;
  }
  if (p1.length < MIN_PWD) {
    errorRegister.value = `密码至少 ${MIN_PWD} 位`;
    return;
  }
  if (p1 !== p2) {
    errorRegister.value = '两次输入的密码不一致';
    return;
  }
  loadingRegister.value = true;
  try {
    const res = await axios.post('/api/register', {
      account: parsed.account,
      code: c,
      password: p1,
      password_confirm: p2,
    });
    if (res?.data?.code !== 0) throw new Error(res?.data?.message || '注册失败');
    const data = res.data.data;
    setAuth(data.token, data.user);
    afterAuthSuccess('register');
  } catch (err) {
    errorRegister.value = err?.response?.data?.message || err.message || '注册失败';
  } finally {
    loadingRegister.value = false;
  }
};
</script>
